import { Link } from "wouter";
import { motion } from "framer-motion";
import logo from "../../assets/stock_images/logo.png";
import playStoreIconSvg from "../../assets/play-store-icon.svg";
import appStoreIconSvg from "../../assets/app-store-icon.svg";
import { Store, MapPin, Mail, Instagram, Twitter, Facebook, ExternalLink, ShieldCheck, ChevronRight, Smartphone, Apple } from "lucide-react";
import { Badge } from "../ui/Badge.js";
import { useToast } from "../../hooks/use-toast.js";

const Footer = () => {
  const { toast } = useToast();

  const handleAppStoreClick = (e) => {
    e.preventDefault();
    toast({
      title: "Coming Soon",
      description: "Quro for iOS is coming soon to the App Store!",
    });
  };

  const PlayStoreIcon = () => (
    <img src={playStoreIconSvg} alt="" aria-hidden="true" className="w-6 h-6" />
  );

  const AppStoreIcon = () => (
    <img src={appStoreIconSvg} alt="" aria-hidden="true" className="w-6 h-6" />
  );


  return (
    <footer className="relative py-16 md:py-24 bg-card/30 backdrop-blur-xl border-t border-border/40 mt-auto overflow-hidden">
      {/* Decorative Background Element */}
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-primary/5 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-accent/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-12 mb-20">
          <div className="col-span-1 sm:col-span-2 md:col-span-1 space-y-8">
            <div className="flex items-center gap-3">
              <motion.div
                whileHover={{ rotate: 15, scale: 1.1 }}
                className="w-10 h-10 flex items-center justify-center bg-white rounded-xl shadow-soft"
              >
                <img src={logo} alt="Quro Logo" className="w-7 h-7 object-contain" />
              </motion.div>
              <span className="font-display font-bold text-2xl tracking-tight">Quro</span>
            </div>
            <p className="text-muted-foreground leading-relaxed max-w-xs">
              Empowering local communities by bringing your favorite neighborhood shops directly to your doorstep.
            </p>
            <div className="flex items-center gap-4">
              {[Instagram, Twitter, Facebook].map((Icon, i) => (
                <motion.a
                  key={i}
                  href="#"
                  whileHover={{ y: -3, color: "var(--primary)" }}
                  className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center text-muted-foreground transition-colors hover:bg-primary/10"
                >
                  <Icon className="w-5 h-5" />
                </motion.a>
              ))}
            </div>

            <div className="space-y-4 pt-4">
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Download Our App</p>
              <div className="flex flex-col gap-3">
                <motion.a
                  href="https://play.google.com/store/apps/details?id=com.quro.app&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.02, x: 5 }}
                  className="flex items-center gap-3 px-4 py-2 bg-zinc-950 text-white rounded-xl border border-white/10 hover:bg-black transition-all shadow-lg group"
                >
                  <PlayStoreIcon />
                  <div className="flex flex-col">
                    <span className="text-[9px] uppercase font-bold opacity-60 leading-none">Get it on</span>
                    <span className="text-xs font-bold leading-tight">Google Play</span>
                  </div>
                </motion.a>
                
                <motion.button
                  onClick={handleAppStoreClick}
                  whileHover={{ scale: 1.02, x: 5 }}
                  className="flex items-center gap-3 px-4 py-2 bg-zinc-950 text-white rounded-xl border border-white/10 hover:bg-black transition-all shadow-lg group w-full text-left"
                >
                  <AppStoreIcon />
                  <div className="flex flex-col">
                    <span className="text-[9px] uppercase font-bold opacity-60 leading-none">Download on the</span>
                    <span className="text-xs font-bold leading-tight">App Store</span>
                  </div>
                </motion.button>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <h4 className="font-display font-bold text-lg">Categories</h4>
            <ul className="space-y-4 text-muted-foreground">
              {["Groceries", "Food & Restaurants", "Hardware & Tools", "Boutique Shops"].map((item) => (
                <li key={item}>
                  <a href="#" className="flex items-center group transition-colors hover:text-primary">
                    <span className="w-0 overflow-hidden transition-all group-hover:w-4 group-hover:mr-2">
                       <ChevronRight className="w-4 h-4" />
                    </span>
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-6">
            <h4 className="font-display font-bold text-lg">Company</h4>
            <ul className="space-y-4 text-muted-foreground">
              <li><Link href="/vendors" className="hover:text-primary transition-colors">For Vendors</Link></li>
              <li><Link href="/investors" className="hover:text-primary transition-colors">Investors</Link></li>
              <li><Link href="/contact" className="hover:text-primary transition-colors">Contact Us</Link></li>
              <li><a href="#" className="hover:text-primary transition-colors">About Story</a></li>
              <li><a href="#" className="hover:text-primary transition-colors font-semibold flex items-center gap-2">Careers <Badge variant="secondary" className="text-[10px] px-2 py-0">Hiring</Badge></a></li>
            </ul>
          </div>

          <div className="space-y-6">
            <h4 className="font-display font-bold text-lg">Support & Legal</h4>
            <ul className="space-y-4 text-muted-foreground">
              <li><Link href="/privacy" className="hover:text-primary transition-colors">Privacy Policy</Link></li>
              <li><Link href="/terms" className="hover:text-primary transition-colors">Terms of Service</Link></li>
              <li><Link href="/vendor-terms" className="hover:text-primary transition-colors">Vendor Terms</Link></li>
              <li><Link href="/delete-account" className="hover:text-destructive transition-colors flex items-center gap-2">Delete Account</Link></li>
              <li className="pt-2">
                 <div className="p-4 rounded-2xl bg-muted/50 border border-border/50 space-y-3">
                   <p className="text-xs font-bold text-foreground flex items-center gap-2">
                     <Mail className="w-3.5 h-3.5 text-primary" /> Support Email
                   </p>
                   <a href="mailto:q@quro.today" className="text-sm font-medium hover:text-primary transition-colors block">q@quro.today</a>
                 </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-10 border-t border-border/50">
           <div className="grid md:grid-cols-2 gap-8 items-start">
              <div className="space-y-4">
                 <div className="flex flex-wrap gap-6 text-xs font-bold text-muted-foreground uppercase tracking-widest">
                    <div className="flex items-center gap-2">
                      <Store className="w-4 h-4 text-primary" /> 10,000+ Vendors
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-primary" /> Hyperlocal Network
                    </div>
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-primary" /> Secured Platform
                    </div>
                 </div>
                 <p className="text-sm text-muted-foreground font-medium">
                   © {new Date().getFullYear()} Quro Infrastructure. Crafted with passion for India.
                 </p>
                 <p className="text-xs text-muted-foreground font-medium">
                   <span className="font-bold uppercase tracking-widest opacity-50">FSSAI Reg. No.</span> 30260424124243466
                 </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:justify-items-end text-sm text-muted-foreground">
                 <div className="space-y-1">
                   <p className="font-bold text-foreground text-xs uppercase opacity-50">Regd. Office</p>
                   <p>Quick Retail Online Private Limited</p>
                   <p>24-21, Ashoknagar,</p>
                   <p>Ramachandra Puram,</p>
                   <p>Hyderabad-502032</p>
                 </div>
                 <div className="space-y-1">
                   <p className="font-bold text-foreground text-xs uppercase opacity-50">HQ Office</p>
                   <p>Suite 406, 4th floor, MyScape Weave,</p>
                   <p>Financial District, Nankramaguda,</p>
                   <p>Hyderabad-500032</p>
                 </div>
              </div>
           </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
